import { useAuth } from "@/lib/auth";
import { Suspense, lazy, useEffect, useState } from "react";

import { config } from "@/lib/config";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AppLoadingState } from "@/components/ui/AppLoadingState";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import { useUIStore } from "@/stores/ui-store";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";

// Lazy load page components for better performance
const Dashboard = lazy(() =>
  import("@/pages/Dashboard").then((module) => ({ default: module.Dashboard })),
);
const BookingFlows = lazy(() =>
  import("@/pages/BookingFlows").then((module) => ({
    default: module.BookingFlows,
  })),
);
const Team = lazy(() =>
  import("@/pages/Team").then((module) => ({ default: module.Team })),
);
const Analytics = lazy(() =>
  import("@/pages/Analytics").then((module) => ({ default: module.Analytics })),
);
const Settings = lazy(() =>
  import("@/pages/Settings").then((module) => ({ default: module.Settings })),
);
const Login = lazy(() =>
  import("@/pages/Login").then((module) => ({ default: module.Login })),
);
const Onboarding = lazy(() =>
  import("@/pages/Onboarding").then((module) => ({
    default: module.Onboarding,
  })),
);
const InviteAccept = lazy(() =>
  import("@/pages/InviteAccept").then((module) => ({
    default: module.InviteAccept,
  })),
);
const MyAvailability = lazy(() =>
  import("@/pages/MyAvailability").then((module) => ({
    default: module.MyAvailability,
  })),
);
const SetAvailability = lazy(() =>
  import("@/pages/SetAvailability").then((module) => ({
    default: module.SetAvailability,
  })),
);

// Reusable page loading component
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <LoadingSpinner size="lg" className="mx-auto mb-4" />
      <p className="text-text-secondary">Loading page...</p>
    </div>
  </div>
);

// Helper to check if current path is an invitation route
function isInvitationRoute(pathname: string): boolean {
  return pathname.startsWith("/invite/") || pathname === "/join";
}

export function App() {
  const { isLoaded, isSignedIn } = useAuth();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLastVisitedPage, setCurrentTenant, currentTenant } = useUIStore(
    (state) => ({
      setLastVisitedPage: state.setLastVisitedPage,
      setCurrentTenant: state.setCurrentTenant,
      currentTenant: state.currentTenant,
    }),
  );

  const [isTenantLoading, setIsTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);

  // Check if we're on an invitation route - these should bypass normal tenant loading
  const onInvitationRoute = isInvitationRoute(location.pathname);

  // Initialize tenant on app startup
  useEffect(() => {
    const initializeTenant = async () => {
      if (!isLoaded || !isSignedIn) return;

      // Skip tenant initialization for invitation routes - they handle their own flow
      if (onInvitationRoute) {
        setIsTenantLoading(false);
        return;
      }

      // Check for pending invitation redirect BEFORE loading tenant
      // This ensures we redirect back to the invitation page immediately after login
      const inviteRedirectUrl = localStorage.getItem("invite_redirect_url");
      if (inviteRedirectUrl) {
        localStorage.removeItem("invite_redirect_url");
        const targetPath = inviteRedirectUrl.replace(
          window.location.origin,
          "",
        );
        setIsTenantLoading(false);
        navigate(targetPath);
        return;
      }

      try {
        setIsTenantLoading(true);
        setTenantError(null);

        // First, try to load from localStorage
        const storedTenant = localStorage.getItem("calendar_admin_tenant");
        if (storedTenant) {
          try {
            const tenant = JSON.parse(storedTenant);
            if (tenant?.slug) {
              setCurrentTenant(tenant.slug);
              setIsTenantLoading(false);
              return;
            }
          } catch (error) {
            console.error("Failed to parse stored tenant:", error);
            localStorage.removeItem("calendar_admin_tenant");
          }
        }

        // If no stored tenant, fetch available tenants
        const response = await authFetch(`${config.apiBaseUrl}/user/tenants`);

        if (response.ok) {
          const data = await response.json();

          if (data.tenants?.length > 0) {
            const firstTenant = data.tenants[0];
            setCurrentTenant(firstTenant.slug);

            // Store in localStorage for next time
            localStorage.setItem(
              "calendar_admin_tenant",
              JSON.stringify({
                id: firstTenant.id,
                slug: firstTenant.slug,
                name: firstTenant.name,
              }),
            );
          } else {
            // No organizations found - user needs onboarding
            setTenantError("NEEDS_ONBOARDING");
          }
        } else {
          console.error("Failed to load tenants:", response.status);
          setTenantError("Failed to load organizations. Please try again.");
        }
      } catch (error) {
        console.error("Error initializing tenant:", error);
        setTenantError("Failed to initialize. Please try again.");
      } finally {
        setIsTenantLoading(false);
      }
    };

    initializeTenant();
  }, [
    isLoaded,
    isSignedIn,
    authFetch,
    setCurrentTenant,
    onInvitationRoute,
    navigate,
  ]);

  // Handle invitation redirect after login (fallback)
  // Primary redirect happens in initializeTenant, this is a backup
  useEffect(() => {
    if (isSignedIn && !isTenantLoading) {
      const inviteRedirectUrl = localStorage.getItem("invite_redirect_url");
      if (inviteRedirectUrl) {
        localStorage.removeItem("invite_redirect_url");
        const targetPath = inviteRedirectUrl.replace(
          window.location.origin,
          "",
        );
        navigate(targetPath);
      }
    }
  }, [isSignedIn, isTenantLoading, navigate]);

  // Track last visited page for better UX
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== "/login" && isSignedIn) {
      setLastVisitedPage(currentPath);
    }
  }, [isSignedIn, setLastVisitedPage]);

  // Show loading spinner while Clerk is initializing
  if (!isLoaded) {
    return (
      <AppLoadingState
        title="Initializing..."
        subtitle="Preparing your secure session"
      />
    );
  }

  // If not authenticated, show login - but for invitation routes, show the invite page
  // which has its own sign-in button that will preserve the URL
  if (!isSignedIn) {
    // For invitation routes, render InviteAccept which handles its own auth flow
    if (onInvitationRoute) {
      return (
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-background">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          <Routes>
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/join" element={<InviteAccept />} />
          </Routes>
        </Suspense>
      );
    }

    return (
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        <Login />
      </Suspense>
    );
  }

  // Show loading while tenant is being initialized (but not for invitation routes)
  if (isTenantLoading && !onInvitationRoute) {
    return (
      <AppLoadingState
        title="Loading your organization..."
        subtitle="Setting up your workspace and preferences"
        showProgress={true}
      />
    );
  }

  // For invitation routes when signed in, render InviteAccept directly
  // This bypasses tenant loading and onboarding checks
  if (onInvitationRoute) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/join" element={<InviteAccept />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Show onboarding if user needs to create an organization
  if (tenantError === "NEEDS_ONBOARDING") {
    return (
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        <Onboarding />
      </Suspense>
    );
  }

  // Show error if tenant initialization failed
  if (tenantError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center flex flex-col items-center justify-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-error/10 rounded-2xl flex items-center justify-center mb-6">
            <div className="w-8 h-8 bg-error rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Something went wrong
          </h2>
          <p className="text-error mb-6 text-sm">{tenantError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary text-text-inverse rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Don't render the main app until we have a tenant
  if (!currentTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center flex flex-col items-center justify-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-warning/10 rounded-2xl flex items-center justify-center mb-6">
            <div className="w-8 h-8 bg-warning rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            No Organization Selected
          </h2>
          <p className="text-text-secondary mb-6 text-sm">
            Please select an organization to continue
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary text-text-inverse rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public invitation routes (outside layout) */}
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/join" element={<InviteAccept />} />

          {/* Main app routes (inside layout) */}
          <Route
            path="/*"
            element={
              <Layout>
                <ErrorBoundary>
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/booking-flows" element={<BookingFlows />} />
                    <Route path="/team" element={<Team />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route
                      path="/custom-availability"
                      element={<MyAvailability />}
                    />
                    <Route path="/availability" element={<SetAvailability />} />

                    {/* Catch all route */}
                    <Route
                      path="*"
                      element={<Navigate to="/dashboard" replace />}
                    />
                  </Routes>
                </ErrorBoundary>
              </Layout>
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
